import React from 'react';
import styled from 'styled-components';
import { Button, Icon, GetColor, ColorUtils, type IconProps, type ButtonProps } from 'venn-ui-kit';
import type { BasicTableProps, BasicTableColumn } from 'venn-components';
import { BasicTable } from 'venn-components';

import { cn } from '@/lib/utils';

export const StyledWrapper = ({ children, className, ...props }: React.BaseHTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn([
      'rounded-[4px] border border-solid border-venn-grey py-[40px] px-[60px] mt-[20px] [&_.visible]:top-[200px]',
      className,
    ])}
    {...props}
  >
    {children}
  </div>
);

export const Section = ({ children, className }: React.BaseHTMLAttributes<HTMLDivElement>) => (
  <div className={cn('account-section w-full py-[24px] px-[60px]', className)}>{children}</div>
);

export const WrapperForSections = ({ children, className }: React.BaseHTMLAttributes<HTMLDivElement>) => (
  <StyledWrapper
    className={cn([
      'py-[24px] px-0 [&_.account-section:not(:last-child)]:border-0 [&_.account-section:not(:last-child)]:border-b',
      '[&_.account-section:not(:last-child)]:border-solid [&_.account-section:not(:last-child)]:border-b-venn-grey',
      className,
    ])}
  >
    {children}
  </StyledWrapper>
);

export const FlexWrapper = ({ children, className }: React.BaseHTMLAttributes<HTMLDivElement>) => (
  <div className={cn('flex', className)}>{children}</div>
);

export const BaselineAlignedFlexWrapper = ({ children, className }: React.BaseHTMLAttributes<HTMLDivElement>) => (
  <div className={cn('flex items-baseline leading-[14px]', className)}>{children}</div>
);

export const InfoWrapper = ({ children, className }: React.BaseHTMLAttributes<HTMLDivElement>) => (
  <div className={cn('flex flex-col ml-[35px] justify-center', className)}>{children}</div>
);

export const Name = ({ children, className }: React.BaseHTMLAttributes<HTMLDivElement>) => (
  <div className={cn('text-[20px] font-bold mr-[11px]', className)}>{children}</div>
);

export const Info = ({ children, className }: React.BaseHTMLAttributes<HTMLDivElement>) => (
  <div className={cn('text-[14px] text-venn-grey-90', className)}>{children}</div>
);

export const FlexRow = ({ children, className }: React.BaseHTMLAttributes<HTMLDivElement>) => (
  <div className={cn('flex justify-between items-center relative', className)}>{children}</div>
);

export const StyledButton = ({ children, className, ...rest }: ButtonProps) => (
  <Button className={cn('h-[40px] min-w-[170px]', className)} {...rest}>
    {children}
  </Button>
);

export const StyledIcon = (props: IconProps) => (
  <Icon className={cn('text-[20px] mr-[8px]', props.className)} {...props} />
);

export const SmallName = ({ children, className }: React.BaseHTMLAttributes<HTMLDivElement>) => (
  <div className={cn('text-[12px] font-bold mr-[5px]', className)}>{children}</div>
);

export const TableName = ({ children, className }: React.BaseHTMLAttributes<HTMLDivElement>) => (
  <div className={cn('text-[20px] font-bold mr-[11px] mb-[10px]', className)}>{children}</div>
);

export const ResultHint = ({ children, className }: React.BaseHTMLAttributes<HTMLDivElement>) => (
  <div className={cn('pt-[20px] text-venn-grey-80', className)}>{children}</div>
);

export const StyledTable = styled(<T extends BasicTableColumn<K>, K>(props: BasicTableProps<T, K>) => (
  <BasicTable<T, K> {...props} />
))`
  thead tr {
    border-bottom: 1px solid ${GetColor.Black};
    th {
      color: ${GetColor.Black};
    }
  }
  tbody > tr {
    font-size: 14px;
    border-bottom: 1px solid ${GetColor.GreyScale.Grey20};

    &:hover {
      background-color: ${ColorUtils.hex2rgbaFrom(GetColor.Primary.Main, 0.1)};
      .members-menu-trigger {
        color: ${GetColor.Black};
      }
    }
  }
  tbody > tr > td > span {
    display: flex;
  }
  .avatar {
    margin-right: 1px;
    margin-bottom: 1px;
  }
`;

export const Cell = styled.span<{ default?: boolean }>`
  &[role='button'] {
    cursor: pointer;
  }
  color: ${(props) => (props.default ? GetColor.HighlightDark : GetColor.Black)};
  height: 34px;
  line-height: 34px;
  display: block;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  @media (max-width: 1100px) {
    max-width: 500px;
    padding-right: 10px;
  }
`;
